/**
 * E05-F06-T03 — the reusable universal send dialog (Stage 4/5/6).
 *
 * 1:1 port of the legacy `dashboard.js` openSendEmailDialog + _addRecipient +
 * showStaffQuickPick + renderSourcingTeamSelector (project-docs.js). One dialog
 * reused for every comm item (client_followup / sourcing_sheet / kickoff_sheet /
 * internal_notification / final_package):
 *
 *   - To / CC / BCC / Subject / Body fields (CC/BCC optional, on every item —
 *     E05-F06-T11); each recipient field shows every address as its own chip
 *     (`RecipientField`);
 *   - a staff quick-pick: a category `<select>` (pre-filtered per item_key — e.g.
 *     `sourcer` for the Sourcing Sheet) + clickable contact chips that add the
 *     contact to To WITHOUT duplicates (`addRecipient`, case-insensitive);
 *   - a sourcing-team `<select>` whose pick REPLACES To with the team's member
 *     emails (`recipientsFromTeam`);
 *   - Send posts the universal Stage-5/6 payload (`{ item_key, email }`) and on
 *     success toasts + closes.
 *
 * Recipient de-dup + team→To live in `@eer/shared/email-send`; the category filter
 * is the web-only `sendEmailModel.ts`. No `innerHTML` — every value is React-text.
 */
import { useMemo, useState, type JSX } from 'react';
import {
  addRecipient,
  appendSignature,
  isValidEmail,
  parseRecipients,
  recipientsFromTeam,
  shouldAppendSignature,
  STAFF_CONTACT_CATEGORIES,
  STAFF_CONTACT_CATEGORY_LABELS,
  type ProjectSourcingTeamRef,
  type Stage5ItemKey,
  type StaffContactCategory,
} from '@eer/shared';
import { Dialog } from '../ui/Dialog.js';
import { RichTextEditor } from '../ui/RichTextEditor.js';
import { RichTextView } from '../ui/RichTextView.js';
import { errorMessage } from '../api/client.js';
import { useToast } from '../ui/ToastContext.js';
import { useStaff } from '../admin/staffApi.js';
import { useSourcingTeams } from '../admin/sourcingTeamsApi.js';
import { useProjectContacts } from '../contacts/directoryApi.js';
import { useUpdateProject } from '../project/projectDetailApi.js';
import { useSendStage5 } from './docGenerationApi.js';
import { defaultQuickPickCategory, filterStaffByCategory } from './sendEmailModel.js';
import { RecipientField } from './RecipientField.js';

const FIELD_CLASS =
  'w-full rounded-md border border-[var(--color-border)] p-2 text-sm outline-none focus:border-[var(--color-accent)]';

export interface SendEmailDialogProps {
  /** Project the email belongs to (path of POST /projects/:id/email/send). */
  readonly projectId: number;
  /** Which comm item — drives the artifact mark + the quick-pick default category. */
  readonly itemKey: Stage5ItemKey;
  readonly open: boolean;
  readonly onClose: () => void;
  /** Dialog heading (e.g. "Send Sourcing Sheet"). */
  readonly title?: string;
  /** Optional initial field values (a pre-composed draft). */
  readonly initialTo?: string;
  readonly initialCc?: readonly string[];
  readonly initialBcc?: readonly string[];
  readonly initialSubject?: string;
  readonly initialBody?: string;
  /**
   * Recruiter email signature. Appended to the sent body (and shown as a preview)
   * ONLY for the signature items (`client_followup_email` / `final_package`) —
   * legacy `sigItems`. Blank/omitted → no signature, no error.
   */
  readonly signature?: string;
  /**
   * The sourcing team already assigned to the project (E03-F08-T04 / ISSUE-4).
   * When set (and the item shows the team selector), the dialog pre-selects this
   * team and seeds `To` with its members — instead of forcing the user to pick
   * the team again in the popup. Ignored for items without a team selector.
   */
  readonly initialSourcingTeam?: ProjectSourcingTeamRef | null;
  /** Fired after a successful send (host flips the stage card to Sent). */
  readonly onSent?: () => void;
}

/** Client-facing items: To holds the client, internal staff chips go to CC. */
const CLIENT_FACING_ITEMS: readonly Stage5ItemKey[] = ['client_followup_email', 'final_package'];

/** Sourcing-team selector is shown only for the sourcing-facing items (legacy). */
const TEAM_SELECTOR_ITEMS: readonly Stage5ItemKey[] = ['kickoff_sheet', 'sourcing_sheet_email'];

export function SendEmailDialog({
  projectId,
  itemKey,
  open,
  onClose,
  title = 'Send Email',
  initialTo = '',
  initialCc = [],
  initialBcc = [],
  initialSubject = '',
  initialBody = '',
  signature = '',
  initialSourcingTeam = null,
  onSent,
}: SendEmailDialogProps): JSX.Element {
  const toast = useToast();
  const staffQuery = useStaff();
  const teamsQuery = useSourcingTeams();
  const send = useSendStage5(projectId);
  // KS3-05 — the project's OWN addressee card is a recipient source here too. The
  // dialog offered staff and sourcing teams but not the people the email is
  // actually about, so a second client contact had to be typed out by hand every
  // time (KS-02 made that list authoritative; KS2-08 made it the send address).
  // Client-facing items only: on an internal document (sourcing sheet, kickoff
  // sheet, internal notification) a client chip read as "this goes to the
  // client" (Ксения 26.10), so it is neither fetched nor shown there.
  const clientFacing = CLIENT_FACING_ITEMS.includes(itemKey);
  const contactsQuery = useProjectContacts(clientFacing ? projectId : null);
  const updateProject = useUpdateProject();

  // Whether this item shows the sourcing-team selector — computed up-front so the
  // team pre-fill (E03-F08-T04 / ISSUE-4) only applies to team-facing items.
  const teamSelectorItem = TEAM_SELECTOR_ITEMS.includes(itemKey);

  const [to, setTo] = useState(() => {
    if (initialTo) return initialTo;
    // No explicit recipients but a team is already assigned → seed To from its
    // members (a team pick otherwise REPLACES To with the same list).
    if (teamSelectorItem && initialSourcingTeam) {
      return recipientsFromTeam(initialSourcingTeam.members);
    }
    return initialTo;
  });
  const [cc, setCc] = useState(initialCc.join(', '));
  const [bcc, setBcc] = useState(initialBcc.join(', '));
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [category, setCategory] = useState<string>(() => defaultQuickPickCategory(itemKey));
  // Pre-select the project's assigned sourcing team (ISSUE-4) instead of forcing
  // the user to re-pick it in the popup.
  const [teamId, setTeamId] = useState<string>(() =>
    teamSelectorItem && initialSourcingTeam ? String(initialSourcingTeam.id) : '',
  );

  const teams = teamsQuery.data ?? [];
  const quickPick = useMemo(
    () => filterStaffByCategory(staffQuery.data ?? [], category),
    [staffQuery.data, category],
  );

  // Team options for the selector. `useSourcingTeams()` returns ACTIVE teams only,
  // so an assigned-but-soft-deleted team would vanish from the list; synthesize an
  // "(inactive)" option for it so the pre-selected value always resolves to a
  // visible option (mirrors SourcingTeamCard).
  const teamOptions = useMemo(() => {
    const base = teams.map((t) => ({ id: t.id, name: t.name, count: t.members.length }));
    if (initialSourcingTeam && !teams.some((t) => t.id === initialSourcingTeam.id)) {
      base.push({
        id: initialSourcingTeam.id,
        name: `${initialSourcingTeam.name} (inactive)`,
        count: initialSourcingTeam.members.length,
      });
    }
    return base;
  }, [teams, initialSourcingTeam]);

  // Per-item scoping (legacy openSendEmailDialog branches):
  //   - CC + BCC on every item (E05-F06-T11 — legacy had CC on the follow-up only;
  //     Ksenia sends the package to several people with copies);
  //   - internal staff chips go to CC on the CLIENT-facing items (follow-up, final
  //     package — To holds the client), to To on the team-facing ones;
  //   - sourcing-team selector only for kickoff_sheet / sourcing_sheet_email;
  //   - signature append/preview only for client_followup_email / final_package.
  const showCc = clientFacing;
  const showTeamSelector = teamSelectorItem;
  const showSignature = shouldAppendSignature(itemKey) && signature.trim().length > 0;

  /**
   * Add a staff contact WITHOUT duplicates (legacy _addRecipient). PK-QA-28:
   * internal staff chips are CC recipients, not the primary To — for the
   * client-facing follow-up email `To` holds the client contact, so chips go to
   * `CC`. On the team-facing items (kickoff_sheet / sourcing_sheet_email) the
   * recipients ARE internal (seeded into To via the team selector), so there the
   * chip keeps adding to `To`.
   */
  function addContact(email: string): void {
    if (showCc) {
      setCc((prev) => addRecipient(prev, email));
    } else {
      setTo((prev) => addRecipient(prev, email));
    }
  }

  /**
   * KS3-05 — the project's addressees, ready to be added with one click.
   *
   * `primary` first, then the CC contacts; a contact without an email is dropped
   * rather than shown as an unusable chip (the addressee card already tells the
   * user that story, this dialog only sends).
   */
  const projectContacts = useMemo(() => {
    const data = contactsQuery.data;
    if (!data) return [] as { id: number; name: string; email: string; primary: boolean }[];
    const rows: { id: number; name: string; email: string; primary: boolean }[] = [];
    if (data.primary?.email) {
      rows.push({
        id: data.primary.id,
        name: data.primary.name ?? '',
        email: data.primary.email,
        primary: true,
      });
    }
    for (const c of data.cc) {
      if (c.email) rows.push({ id: c.id, name: c.name ?? '', email: c.email, primary: false });
    }
    return rows;
  }, [contactsQuery.data]);

  /**
   * A project contact goes to `To` when it is the main addressee and to `CC`
   * otherwise — the same split the addressee card stores. Both fields stay editable,
   * so a one-off exception is still one click plus typing.
   */
  function addProjectContact(email: string, primary: boolean): void {
    if (primary) setTo((prev) => addRecipient(prev, email));
    else setCc((prev) => addRecipient(prev, email));
  }

  /** A team pick REPLACES To with the team's member emails (legacy _onSrcTeamSelect). */
  function selectTeam(id: string): void {
    setTeamId(id);
    if (!id) return;
    const team = teams.find((t) => String(t.id) === id);
    if (team) setTo(recipientsFromTeam(team.members));
  }

  async function onSend(): Promise<void> {
    // Email-regex validation of recipients (legacy: every To address must match).
    const recipients = parseRecipients(to);
    if (recipients.length === 0 || !recipients.every(isValidEmail)) {
      toast.error('Enter valid recipient email(s)');
      return;
    }
    const ccList = parseRecipients(cc);
    const bccList = parseRecipients(bcc);
    if (!ccList.every(isValidEmail)) {
      toast.error('Enter valid CC email(s)');
      return;
    }
    if (!bccList.every(isValidEmail)) {
      toast.error('Enter valid BCC email(s)');
      return;
    }
    // Append the recruiter signature for signature items (blank → unchanged).
    const bodyToSend = shouldAppendSignature(itemKey) ? appendSignature(body, signature) : body;
    try {
      await send.mutateAsync({
        item_key: itemKey,
        email: {
          to: to.trim(),
          subject,
          body: bodyToSend,
          cc: ccList,
          bcc: bccList,
        },
      });
      // KS3-05 — remember WHO this document goes to, keyed by item.
      //
      // Ксения: «сохранять это, чтобы не сбрасывалось, если повторно отправить
      // надо». Saved AFTER a successful send on purpose: the set that actually
      // reached the client is the one worth restoring, and a send that failed
      // should not quietly redefine the project's addressees.
      //
      // Best-effort: the email is already gone, so a failed bookkeeping write must
      // not be reported as a failed send.
      try {
        await updateProject.mutateAsync({
          projectId,
          updates: {
            email_recipients: {
              [itemKey]: {
                to: recipients,
                cc: ccList,
                ...(bccList.length > 0 ? { bcc: bccList } : {}),
              },
            },
          },
        });
      } catch {
        /* адресаты не сохранились — письмо ушло, молчим */
      }
      toast.success('Email sent');
      onSent?.();
      onClose();
    } catch (error) {
      // PK-QA-29 — surface the REAL server error (503 NoSenderMailboxError /
      // 502 EmailSendError with their message/code) instead of a generic
      // 'Send failed'. `errorMessage` reads the thrown `ApiResultError.detail.message`
      // (the same body-parse the rest of the app funnels query errors through),
      // falling back to 'Send failed' only when no message is present.
      toast.error(errorMessage(error, 'Send failed'));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      testId="send-email-dialog"
      maxWidth="2xl"
      footer={
        <>
          <button
            type="button"
            data-testid="send-email-cancel"
            onClick={onClose}
            className="rounded border border-[var(--color-border)] bg-white px-4 py-2 text-sm font-medium hover:bg-[var(--color-surface-muted)]"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="send-email-send"
            onClick={() => void onSend()}
            disabled={send.isPending}
            className="rounded bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--color-primary-hover)] disabled:opacity-60"
          >
            {send.isPending ? 'Sending…' : 'Send'}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* KS3-05 — the project's own addressees. Listed FIRST: for a client-facing
            email these are the people the message is about, and they were the one
            source the dialog did not offer. */}
        {clientFacing && projectContacts.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-[var(--color-text-strong)]">
              Project contacts
            </span>
            <div className="flex flex-wrap gap-1.5" data-testid="send-email-project-contacts">
              {projectContacts.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid="send-email-contact-chip"
                  data-email={c.email}
                  onClick={() => addProjectContact(c.email, c.primary)}
                  className="rounded-full border border-[var(--color-border)] bg-white px-2.5 py-1 text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)]"
                  title={`${c.name} <${c.email}>`}
                >
                  {c.name || c.email}
                  {c.primary && (
                    <span className="ml-1 text-[10px] uppercase text-[var(--color-primary)]">
                      main
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quick-pick: category filter + clickable staff chips */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[var(--color-text-strong)]">Quick add</span>
            <select
              data-testid="send-email-category"
              className="rounded border border-[var(--color-border)] px-2 py-1 text-xs"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              aria-label="Staff category filter"
            >
              <option value="all">All categories</option>
              {STAFF_CONTACT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {STAFF_CONTACT_CATEGORY_LABELS[c as StaffContactCategory]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="send-email-quickpick">
            {quickPick.length === 0 ? (
              <span className="text-xs text-[var(--color-text-muted)]">
                {staffQuery.isLoading ? 'Loading…' : 'No contacts in this category'}
              </span>
            ) : (
              quickPick.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid="send-email-staff-chip"
                  data-email={c.email}
                  onClick={() => addContact(c.email)}
                  className="rounded-full border border-[var(--color-border)] bg-white px-2.5 py-1 text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)]"
                  title={`${c.name} <${c.email}>`}
                >
                  {c.name || c.email}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Sourcing-team selector (replaces To with the team's members) —
            only for kickoff_sheet / sourcing_sheet_email (legacy scoping). */}
        {showTeamSelector && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[var(--color-text-strong)]">Sourcing team</span>
            <select
              data-testid="send-email-team"
              className={FIELD_CLASS}
              value={teamId}
              onChange={(e) => selectTeam(e.target.value)}
            >
              <option value="">— Select a sourcing team —</option>
              {teamOptions.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name} ({t.count})
                </option>
              ))}
            </select>
          </label>
        )}

        {/* To / CC / BCC — every address shown separately (E05-F06-T11). */}
        <RecipientField label="To" testId="send-email-to" value={to} onChange={setTo} />
        <RecipientField
          label="CC"
          testId="send-email-cc"
          value={cc}
          onChange={setCc}
          placeholder="optional"
        />
        <RecipientField
          label="BCC"
          testId="send-email-bcc"
          value={bcc}
          onChange={setBcc}
          placeholder="optional"
        />

        {/* Subject */}
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">Subject</span>
          <input
            type="text"
            data-testid="send-email-subject"
            className={FIELD_CLASS}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
        </label>

        {/* Body */}
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">Body</span>
          {/* Этап 4 / KS-07: тело письма — HTML. Клиент получает ровно то, что
              видно здесь: цвет, списки и картинку подписи markdown не выражал. */}
          <RichTextEditor
            testId="send-email-body"
            ariaLabel="Email body"
            minHeightClass="min-h-[140px]"
            value={body}
            onChange={setBody}
          />
        </label>

        {/* Signature preview — appended to the sent body for signature items. */}
        {showSignature && (
          <div
            data-testid="send-email-signature"
            className="mt-1 border-t border-[var(--color-border)] pt-2 text-xs text-[var(--color-text-muted)]"
          >
            <div className="mb-1 text-[var(--color-text-muted)]">— Signature —</div>
            {/* Подпись тоже HTML (может содержать логотип), поэтому <pre> заменён
                на санитизирующий рендер — иначе теги были бы видны как текст. */}
            <RichTextView value={signature} testId="send-email-signature-body" />
          </div>
        )}
      </div>
    </Dialog>
  );
}

export default SendEmailDialog;
